import React, { useState } from 'react';
import { Button } from './Button';
import FavoriteButton from './FavoriteButton';
import ContentChips from './ContentChips';
import { ArrowLeftIcon, BookOpenIcon, BrainIcon, HeadphonesIcon, ClockIcon, PlayIcon, StarIcon, CheckIcon, InfoIcon, DownloadIcon, ShareIcon } from 'lucide-react';
interface ThemeDetailProps {
  themeId: string;
  onBack: () => void;
  onStartExercise: (exerciseId: string) => void;
}
interface Exercise {
  id: string;
  title: string;
  description: string;
  duration: string;
  type: 'meditation' | 'exercise' | 'reading';
  completed?: boolean;
  icon: React.ReactNode;
  difficulty: 1 | 2 | 3; // 1 = beginner, 2 = intermediate, 3 = advanced
  tags: string[];
}
const ThemeDetail: React.FC<ThemeDetailProps> = ({
  themeId,
  onBack,
  onStartExercise
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  // Mock theme data
  const themes = {
    stress: {
      id: 'stress',
      title: 'Stress & burn-out',
      description: 'Stress is een natuurlijke reactie van je lichaam op uitdagingen, maar langdurige stress kan leiden tot burn-out. Deze oefeningen helpen je om stress te herkennen, te verminderen en beter met druk om te gaan.',
      coverImage: null,
      exerciseCount: 12,
      completedCount: 3,
      tags: ['stress', 'werk', 'ontspanning']
    },
    anxiety: {
      id: 'anxiety',
      title: 'Angst',
      description: 'Angstgevoelens kunnen je dagelijks leven beïnvloeden. Deze oefeningen helpen je om angst te begrijpen, te accepteren en ermee om te gaan, zodat je meer rust en controle ervaart.',
      coverImage: null,
      exerciseCount: 14,
      completedCount: 0,
      tags: ['angst', 'emoties', 'ontspanning']
    },
    sleep: {
      id: 'sleep',
      title: 'Slaap',
      description: 'Goede slaap is essentieel voor je mentale en fysieke gezondheid. Deze oefeningen helpen je om je slaapkwaliteit te verbeteren, makkelijker in slaap te vallen en uitgerust wakker te worden.',
      coverImage: null,
      exerciseCount: 8,
      completedCount: 2,
      tags: ['slaap', 'ontspanning', 'avondroutine']
    },
    focus: {
      id: 'focus',
      title: 'Focus & concentratie',
      description: 'In een wereld vol afleiding kan het moeilijk zijn om je te concentreren. Deze oefeningen helpen je om je aandacht te trainen, beter te focussen en productiever te zijn.',
      coverImage: null,
      exerciseCount: 10,
      completedCount: 1,
      tags: ['focus', 'werk', 'productiviteit']
    },
    relationships: {
      id: 'relationships',
      title: 'Relaties',
      description: 'Gezonde relaties zijn belangrijk voor je welzijn. Deze oefeningen helpen je om je communicatie te verbeteren, grenzen te stellen en diepere verbindingen te maken met anderen.',
      coverImage: null,
      exerciseCount: 7,
      completedCount: 0,
      tags: ['relaties', 'communicatie', 'sociaal']
    },
    mindfulness: {
      id: 'mindfulness',
      title: 'Mindfulness',
      description: 'Mindfulness helpt je om meer in het moment te leven en minder te piekeren. Deze oefeningen leren je om bewuster te zijn van je gedachten, gevoelens en omgeving.',
      coverImage: null,
      exerciseCount: 15,
      completedCount: 5,
      tags: ['mindfulness', 'ontspanning', 'bewustzijn']
    }
  };
  // Mock exercises data
  const exercisesData: Record<string, Exercise[]> = {
    stress: [{
      id: 'stress-1',
      title: 'Ademhalingsoefening',
      description: 'Een eenvoudige maar effectieve techniek om snel stress te verminderen',
      duration: '3 min',
      type: 'exercise',
      icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-1317" />,
      difficulty: 1,
      tags: ['beginner', 'snel', 'ademhaling'],
      completed: true
    }, {
      id: 'stress-2',
      title: 'Bodyscan meditatie',
      description: 'Scan systematisch je lichaam om spanning te herkennen en los te laten',
      duration: '8 min',
      type: 'meditation',
      icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-1318" />,
      difficulty: 2,
      tags: ['meditatie', 'ontspanning'],
      completed: true
    }, {
      id: 'stress-3',
      title: 'Grenzen stellen',
      description: 'Leer hoe je gezonde grenzen kunt stellen om stress te voorkomen',
      duration: '5 min',
      type: 'reading',
      icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-1319" />,
      difficulty: 2,
      tags: ['werk', 'relaties', 'grenzen']
    }, {
      id: 'stress-4',
      title: 'Progressieve spierontspanning',
      description: 'Ontspan systematisch je spieren om fysieke spanning te verminderen',
      duration: '10 min',
      type: 'exercise',
      icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-1320" />,
      difficulty: 1,
      tags: ['ontspanning', 'lichamelijk'],
      completed: true
    }, {
      id: 'stress-5',
      title: 'Stresspatronen herkennen',
      description: 'Leer je persoonlijke stresspatronen te identificeren',
      duration: '7 min',
      type: 'reading',
      icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-1321" />,
      difficulty: 2,
      tags: ['inzicht', 'zelfkennis']
    }],
    anxiety: [{
      id: 'anxiety-1',
      title: 'Angstreductie ademhaling',
      description: 'Kalm je zenuwstelsel met deze gerichte ademhalingstechniek',
      duration: '4 min',
      type: 'exercise',
      icon: <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-1322" />,
      difficulty: 1,
      tags: ['ademhaling', 'snel', 'beginner']
    }, {
      id: 'anxiety-2',
      title: 'Angstgedachten uitdagen',
      description: 'Leer hoe je angstige gedachten kunt herkennen en uitdagen',
      duration: '6 min',
      type: 'reading',
      icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-1323" />,
      difficulty: 2,
      tags: ['cognitief', 'gedachten']
    }],
    sleep: [{
      id: 'sleep-1',
      title: 'Avondroutine opbouwen',
      description: 'Creëer een effectieve routine om je lichaam voor te bereiden op slaap',
      duration: '5 min',
      type: 'reading',
      icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-1324" />,
      difficulty: 1,
      tags: ['routine', 'avond', 'beginner'],
      completed: true
    }, {
      id: 'sleep-2',
      title: 'Slaapmeditatie',
      description: 'Ontspan je lichaam en geest om makkelijker in slaap te vallen',
      duration: '12 min',
      type: 'meditation',
      icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-1325" />,
      difficulty: 1,
      tags: ['meditatie', 'ontspanning'],
      completed: true
    }],
    focus: [{
      id: 'focus-1',
      title: 'Pomodoro techniek',
      description: 'Verbeter je focus met deze bewezen tijdsmanagement methode',
      duration: '4 min',
      type: 'reading',
      icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-1326" />,
      difficulty: 1,
      tags: ['productiviteit', 'werk', 'beginner'],
      completed: true
    }],
    relationships: [{
      id: 'rel-1',
      title: 'Actief luisteren',
      description: 'Verbeter je relaties door beter te leren luisteren',
      duration: '6 min',
      type: 'reading',
      icon: <BookOpenIcon className="w-6 h-6 text-gray-700" data-id="element-1327" />,
      difficulty: 2,
      tags: ['communicatie', 'sociaal']
    }],
    mindfulness: [{
      id: 'mind-1',
      title: 'Beginners mindfulness',
      description: 'Een eenvoudige introductie tot mindfulness meditatie',
      duration: '5 min',
      type: 'meditation',
      icon: <HeadphonesIcon className="w-6 h-6 text-gray-700" data-id="element-1328" />,
      difficulty: 1,
      tags: ['meditatie', 'beginner'],
      completed: true
    }]
  };
  const currentTheme = themes[themeId as keyof typeof themes] || themes.stress;
  const exercises = exercisesData[themeId] || exercisesData.stress;
  const filteredExercises = activeFilter === 'all' ? exercises : exercises.filter(exercise => {
    if (activeFilter === 'completed') return exercise.completed;
    if (activeFilter === 'uncompleted') return !exercise.completed;
    if (activeFilter === 'meditation') return exercise.type === 'meditation';
    if (activeFilter === 'exercise') return exercise.type === 'exercise';
    if (activeFilter === 'reading') return exercise.type === 'reading';
    return exercise.tags.includes(activeFilter);
  });
  const renderDifficultyLevel = (level: 1 | 2 | 3) => {
    return <div className="flex items-center" data-id="element-1329">
        {Array(3).fill(0).map((_, i) => <div key={i} className={`w-2 h-2 rounded-full mx-0.5 ${i < level ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-1330"></div>)}
      </div>;
  };
  return <div className="w-full max-w-xl mx-auto py-8" data-id="element-1331">
      <div className="flex items-center mb-8" data-id="element-1332">
        <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-100" aria-label="Terug" data-id="element-1333">
          <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-1334" />
        </button>
        <h1 className="text-2xl font-light text-gray-800" data-id="element-1335">
          {currentTheme.title}
        </h1>
      </div>
      <div className="p-6 border-2 border-gray-300 rounded-xl mb-8" data-id="element-1336">
        <div className="flex justify-between items-start mb-4" data-id="element-1337">
          <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mr-4" data-id="element-1338">
            <BrainIcon className="w-6 h-6 text-gray-700" data-id="element-1339" />
          </div>
          <FavoriteButton data-id="element-1340" />
        </div>
        <p className="text-gray-700 mb-6" data-id="element-1341">{currentTheme.description}</p>
        <div className="flex flex-wrap gap-2 mb-6" data-id="element-1342">
          {currentTheme.tags.map(tag => <span key={tag} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700" data-id="element-1343">
              {tag}
            </span>)}
        </div>
        <div className="flex justify-between items-center" data-id="element-1344">
          <div className="flex items-center" data-id="element-1345">
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1346">
              <div className="bg-gray-700 h-2 rounded-full" style={{
              width: `${currentTheme.completedCount / currentTheme.exerciseCount * 100}%`
            }} data-id="element-1347"></div>
            </div>
            <span className="ml-3 text-sm text-gray-600" data-id="element-1348">
              {currentTheme.completedCount}/{currentTheme.exerciseCount}
            </span>
          </div>
          <div className="flex space-x-2" data-id="element-1349">
            <button className="p-2 rounded-full hover:bg-gray-100" aria-label="Download" onClick={() => alert('Download functionaliteit komt binnenkort')} data-id="element-1350">
              <DownloadIcon className="w-5 h-5 text-gray-600" data-id="element-1351" />
            </button>
            <button className="p-2 rounded-full hover:bg-gray-100" aria-label="Delen" onClick={() => alert('Deel functionaliteit komt binnenkort')} data-id="element-1352">
              <ShareIcon className="w-5 h-5 text-gray-600" data-id="element-1353" />
            </button>
          </div>
        </div>
      </div>
      <div className="mb-8" data-id="element-1354">
        <ContentChips activeChip={activeFilter} onChipClick={setActiveFilter} data-id="element-1355" />
      </div>
      <div className="space-y-4" data-id="element-1356">
        <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1357">Oefeningen</h2>
        {filteredExercises.length > 0 ? filteredExercises.map(exercise => <div key={exercise.id} className="p-5 border-2 border-gray-300 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors duration-200 relative" onClick={() => onStartExercise(exercise.id)} data-id="element-1358">
              <div className="absolute top-4 right-4" data-id="element-1359">
                <FavoriteButton size="sm" data-id="element-1360" />
              </div>
              <div className="flex items-start" data-id="element-1361">
                <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center mr-4" data-id="element-1362">
                  {exercise.icon}
                </div>
                <div data-id="element-1363">
                  <div className="flex items-center mb-1" data-id="element-1364">
                    <h3 className="text-base font-medium text-gray-800 mr-2" data-id="element-1365">
                      {exercise.title}
                    </h3>
                    {exercise.completed && <span className="flex items-center text-xs text-gray-600" data-id="element-1366">
                        <CheckIcon className="w-3 h-3 mr-1" data-id="element-1367" />
                        Voltooid
                      </span>}
                  </div>
                  <div className="flex items-center mb-2 text-xs text-gray-500" data-id="element-1368">
                    <span className="flex items-center mr-3" data-id="element-1369">
                      <ClockIcon className="w-3 h-3 mr-1" data-id="element-1370" />
                      {exercise.duration}
                    </span>
                    <span className="flex items-center mr-3" data-id="element-1371">
                      {exercise.type === 'meditation' && 'Meditatie'}
                      {exercise.type === 'exercise' && 'Oefening'}
                      {exercise.type === 'reading' && 'Leesmateriaal'}
                    </span>
                    <span className="flex items-center" data-id="element-1372">
                      {renderDifficultyLevel(exercise.difficulty)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-1373">
                    {exercise.description}
                  </p>
                </div>
              </div>
            </div>) : <div className="p-8 border-2 border-gray-300 rounded-xl text-center" data-id="element-1374">
            <p className="text-gray-700 mb-4" data-id="element-1375">
              Geen oefeningen gevonden met deze filter
            </p>
            <Button variant="secondary" onClick={() => setActiveFilter('all')} data-id="element-1376">
              Alle oefeningen tonen
            </Button>
          </div>}
      </div>
    </div>;
};
export default ThemeDetail;